import React from 'react';
import { GoogleAddressService } from '../utils/googleAddressService';
import { getEnvApiBaseUrl } from '../config/env';
import type { GoogleAddressResult } from '../utils/googleAddressService';

interface Props {
  onNavigate: (screen: string) => void;
}

// CB:BEGIN eagleview-sandbox-page
const EagleViewSandbox: React.FC<Props> = ({ onNavigate }) => {
  const [input, setInput] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState<GoogleAddressResult | null>(null);
  const [status, setStatus] = React.useState<string>('');
  // CB:BEGIN ev-measurement-failsafe-state
  const [measurementJson, setMeasurementJson] = React.useState<any>(null);
  const [measurementSummary, setMeasurementSummary] = React.useState<{ reportId: string; status: string; emailed: boolean; emailError?: string } | null>(null);
  const [pdfUrl, setPdfUrl] = React.useState<string>('');
  // CB:END ev-measurement-failsafe-state
  // CB:BEGIN ev-sandbox-toggle
  const [useSandbox, setUseSandbox] = React.useState<boolean>(true);
  // CB:END ev-sandbox-toggle
  // CB:BEGIN ev-omaha-dropdown
  const OMAHA_SANDBOX_OPTIONS: GoogleAddressResult[] = React.useMemo(() => ([
    { street: '509 S 58TH ST', city: 'OMAHA', state: 'NE', zipCode: '68106', fullAddress: '509 S 58TH ST, OMAHA, NE 68106' },
    { street: '1005 S 52ND ST', city: 'OMAHA', state: 'NE', zipCode: '68106', fullAddress: '1005 S 52ND ST, OMAHA, NE 68106' },
    { street: '4326 POPPLETON AVE', city: 'OMAHA', state: 'NE', zipCode: '68105', fullAddress: '4326 POPPLETON AVE, OMAHA, NE 68105' },
  ]), []);
  const handleQuickPick = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (!value) return;
    const idx = Number(value);
    if (Number.isNaN(idx)) return;
    const opt = OMAHA_SANDBOX_OPTIONS[idx];
    if (!opt) return;
    setSelected(opt);
    setInput(opt.fullAddress);
    setSuggestions([]);
    setStatus('');
  };
  // CB:END ev-omaha-dropdown
  // CB:BEGIN eagleview-sandbox-property-flow
  const [requestId, setRequestId] = React.useState<string>('');
  const [propertyJson, setPropertyJson] = React.useState<any>(null);
  const [reportId, setReportId] = React.useState<string>('');
  const apiBaseUrl = React.useMemo(() => getEnvApiBaseUrl(), []);
  // CB:END eagleview-sandbox-property-flow

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    setStatus('');
    if (!value.trim()) {
      setSuggestions([]);
      return;
    }
    const preds = await GoogleAddressService.getAutocompleteSuggestions(value);
    setSuggestions(preds);
  };

  const handlePick = async (prediction: any) => {
    setLoading(true);
    try {
      const details = await GoogleAddressService.getPlaceDetails(prediction.place_id);
      setSelected(details);
      setInput(details?.fullAddress || prediction.description || '');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const orderReport = async () => {
    if (!selected?.fullAddress) { setStatus('Please select an address first.'); return; }
    setLoading(true); setStatus(''); setReportId('');
    setMeasurementJson(null); setMeasurementSummary(null);
    try {
      // 1) Place measurement order
      const { street, city, state, zipCode } = selected;
      const referenceId = `sandbox-${Date.now()}`;
      const place = await fetch(`${apiBaseUrl}/api/eagleview/order/place`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: street, city, state, zip: zipCode, country: 'US', referenceId })
      });
      const placeJson = await place.json();
      if (!place.ok) throw new Error(placeJson?.error || `PlaceOrder failed (${place.status})`);
      setStatus('✅ Order placed. Resolving Report ID…');

      // Prefer reportId from place response when present
      let rid = '';
      if (Array.isArray(placeJson) && (placeJson[0]?.ReportIds?.[0] || placeJson[0]?.ReportId)) rid = String(placeJson[0]?.ReportIds?.[0] || placeJson[0]?.ReportId);
      else if (placeJson?.ReportIds?.[0]) rid = String(placeJson.ReportIds[0]);
      else if (placeJson?.ReportId) rid = String(placeJson.ReportId);

      // 2) Poll GetReports by referenceId to resolve ReportId (simple retries)
      if (!rid) {
        for (let i = 0; i < 18; i++) {
          const resp = await fetch(`${apiBaseUrl}/api/eagleview/order/find-report-id`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ referenceId, address: street, city, state, zip: zipCode })
          });
          let j: any = null; try { j = await resp.json(); } catch {}
          if ((j && j.ok) && j.reportId) { rid = String(j.reportId); break; }
          await new Promise(r => setTimeout(r, 5000));
        }
      }
      if (!rid) throw new Error('Could not resolve Report ID yet. Try again in a minute.');
      setReportId(rid);
      setPdfUrl(`${apiBaseUrl}/api/eagleview/report/${encodeURIComponent(rid)}/pdf`);
      setStatus(`✅ ReportId resolved: ${rid}. Checking status…`);

      // 3) Poll report status until completed (bounded retries)
      let completed = false;
      let lastStatusJson: any = null; let displayStatus = 'Unknown';
      for (let i = 0; i < 10; i++) {
        const r = await fetch(`${apiBaseUrl}/api/eagleview/order/get-report?reportId=${encodeURIComponent(rid)}`);
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || `GetReport failed (${r.status})`);
        displayStatus = (Array.isArray(j) ? j[0]?.Status : j?.Status) || (Array.isArray(j) ? j[0]?.DisplayStatus : j?.DisplayStatus) || 'Unknown';
        lastStatusJson = j;
        if (/completed/i.test(displayStatus)) { completed = true; break; }
        await new Promise(r => setTimeout(r, 5000));
      }
      if (!completed) throw new Error('Report not completed yet. Try again later.');
      setMeasurementJson(lastStatusJson);

      // 4) Email the Premium PDF
      const eResp = await fetch(`${apiBaseUrl}/api/eagleview/order/email-pdf`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId: rid, to: 'dev@corebids.com' })
      });
      if (!eResp.ok) {
        const t = await eResp.text();
        setMeasurementSummary({ reportId: rid, status: displayStatus, emailed: false, emailError: `(${eResp.status}) ${t}` });
        setStatus('⚠️ Report completed, but email failed. See details below.');
        return;
      }
      setMeasurementSummary({ reportId: rid, status: displayStatus, emailed: true });
      setStatus('✅ Premium PDF emailed to dev@corebids.com');
    } catch (err: any) {
      setStatus(`❌ ${err?.message || 'Order failed'}`);
    } finally {
      setLoading(false);
    }
  };

  // CB:BEGIN eagleview-sandbox-property-flow
  const requestPropertyData = async () => {
    if (!selected?.fullAddress) {
      setStatus('Please select an address first.');
      return;
    }
    setLoading(true);
    setStatus('');
    try {
      const r = await fetch(`${apiBaseUrl}/api/eagleview/property/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: selected.fullAddress })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || `Request failed (${r.status})`);
      const id = j?.request?.id || '';
      setRequestId(id);
      setStatus(id ? `✅ Requested property data. Request ID: ${id}` : '✅ Requested property data.');
    } catch (e: any) {
      setStatus(`❌ ${e?.message || 'Request failed'}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyResult = async () => {
    if (!requestId) {
      setStatus('Request property data first.');
      return;
    }
    setLoading(true);
    setStatus('');
    try {
      const r = await fetch(`${apiBaseUrl}/api/eagleview/property/result/${encodeURIComponent(requestId)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || `Fetch failed (${r.status})`);
      setPropertyJson(j);
      const complete = j?.request?.status?.toLowerCase?.() === 'complete';
      setStatus(complete ? '✅ Property data is complete.' : `ℹ️ Status: ${j?.request?.status || 'Unknown'}`);
      if (complete) {
        // Email a simple HTML report to dev@corebids.com
        await fetch(`${apiBaseUrl}/api/eagleview/property/email-report`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requestId, to: 'dev@corebids.com' })
        }).catch(() => {});
      }
    } catch (e: any) {
      setStatus(`❌ ${e?.message || 'Fetch failed'}`);
    } finally {
      setLoading(false);
    }
  };

  // Place measurement order (Premium) using the selected address
  const placeMeasurementOrder = async () => {
    if (!selected?.fullAddress) { setStatus('Please select an address first.'); return; }
    setLoading(true); setStatus(''); setReportId('');
    try {
      const { street, city, state, zipCode } = selected;
      const resp = await fetch(`${apiBaseUrl}/api/eagleview/order/place`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: street, city, state, zip: zipCode, country: 'US', referenceId: 'sandbox-' + Date.now() })
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j?.error || `Order failed (${resp.status})`);
      const rid = (Array.isArray(j) && j[0]?.ReportIds?.[0]) || j?.[0]?.OrderId || j?.OrderId || '';
      setReportId(String(rid || ''));
      setStatus(rid ? `✅ Order placed. ReportId: ${rid}` : '✅ Order placed.');
    } catch (e: any) {
      setStatus(`❌ ${e?.message || 'Order failed'}`);
    } finally {
      setLoading(false);
    }
  };

  // Poll report status and email PDF when completed
  const fetchMeasurementReport = async () => {
    if (!reportId) { setStatus('Place an order first.'); return; }
    setLoading(true); setStatus('');
    setMeasurementSummary(null);
    try {
      const r = await fetch(`${apiBaseUrl}/api/eagleview/order/get-report?reportId=${encodeURIComponent(reportId)}`);
      const j = await r.json();
      if (!r.ok) throw new Error(j?.error || `Fetch failed (${r.status})`);
      const displayStatus = (Array.isArray(j) ? j[0]?.Status : j?.Status) || (Array.isArray(j) ? j[0]?.DisplayStatus : j?.DisplayStatus) || 'Unknown';
      setMeasurementJson(j);
      setStatus(`ℹ️ Report status: ${displayStatus}`);
      setPdfUrl(`${apiBaseUrl}/api/eagleview/report/${encodeURIComponent(reportId)}/pdf`);
      if (/completed/i.test(displayStatus)) {
        const eResp = await fetch(`${apiBaseUrl}/api/eagleview/order/email-pdf`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reportId, to: 'dev@corebids.com' })
        });
        if (!eResp.ok) {
          const t = await eResp.text();
          setMeasurementSummary({ reportId, status: displayStatus, emailed: false, emailError: `(${eResp.status}) ${t}` });
          setStatus('⚠️ Report completed, but email failed. See details below.');
          return;
        }
        setMeasurementSummary({ reportId, status: displayStatus, emailed: true });
        setStatus('✅ Premium PDF emailed to dev@corebids.com');
      }
    } catch (e: any) {
      setStatus(`❌ ${e?.message || 'Fetch failed'}`);
    } finally {
      setLoading(false);
    }
  };
  // CB:END eagleview-sandbox-property-flow

  return (
    <div className="container" style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>EagleView Sandbox</h1>
        <button className="btn btn-secondary" onClick={() => onNavigate('landing')} style={{ color: 'black' }}>Back</button>
      </div>

      <p style={{ color: '#475569', marginBottom: 16 }}>Enter an address (Google autocomplete). Select a result, then order a Premium report to send a confirmation email to dev@corebids.com.</p>

      {/* CB:BEGIN ev-sandbox-toggle-ui */}
      <div className="flex items-center gap-2 mb-3">
        <input id="toggle-sandbox" type="checkbox" checked={useSandbox} onChange={(e) => setUseSandbox(e.target.checked)} style={{ width: '8px', height: '8px' }} />
        <label htmlFor="toggle-sandbox" className="text-slate-700 text-sm select-none cursor-pointer">Use sandbox addresses</label>
      </div>
      {/* CB:END ev-sandbox-toggle-ui */}

      {/* CB:BEGIN ev-omaha-dropdown-ui */}
      {useSandbox && (
        <div className="flex items-center gap-2 mb-3">
          <div className="text-slate-500 text-sm">Quick pick (Omaha sandbox):</div>
          <select onChange={handleQuickPick} value="" className="px-3 py-2 border rounded-md">
            <option value="">Select a test address…</option>
            {OMAHA_SANDBOX_OPTIONS.map((opt, i) => (
              <option key={i} value={i}>{opt.fullAddress}</option>
            ))}
          </select>
        </div>
      )}
      {/* CB:END ev-omaha-dropdown-ui */}

      <div style={{ position: 'relative', marginBottom: 16 }}>
        <input
          value={input}
          onChange={handleChange}
          placeholder="Start typing an address…"
          className="w-full px-3 py-3 border rounded-md text-base"
          style={{ borderColor: '#e5e7eb' }}
        />
        {!!suggestions.length && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px', zIndex: 10, maxHeight: 260, overflowY: 'auto' }}>
            {suggestions.map((p) => (
              <button
                key={p.place_id}
                onClick={() => handlePick(p)}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', background: 'white', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
              >
                {p.description}
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="bg-white border rounded-xl p-4 mb-4">
          <h3 className="m-0 mb-3 font-semibold">Property Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="text-slate-500 text-sm mb-1">Full Address</div>
              <div className="font-semibold">{selected.fullAddress}</div>
            </div>
            <div>
              <div className="text-slate-500 text-sm mb-1">Street</div>
              <div>{selected.street}</div>
            </div>
            <div>
              <div className="text-slate-500 text-sm mb-1">City / State</div>
              <div>{selected.city}, {selected.state}</div>
            </div>
            <div>
              <div className="text-slate-500 text-sm mb-1">ZIP</div>
              <div>{selected.zipCode}</div>
            </div>
            <div>
              <div className="text-slate-500 text-sm mb-1">Coordinates</div>
              <div>{selected.coordinates ? `${selected.coordinates.lat.toFixed(6)}, ${selected.coordinates.lng.toFixed(6)}` : '—'}</div>
            </div>
          </div>
          <div className="mt-4 text-slate-600 text-sm">Static imagery will appear here in the full integration.</div>
        </div>
      )}

      <div className="flex gap-3">
        <button className="px-3 py-2 rounded bg-blue-600 text-white" onClick={orderReport} disabled={loading || !selected}>
          {loading ? 'Working…' : 'Order Premium Report (Email to dev@corebids.com)'}
        </button>
        <button className="px-3 py-2 rounded border" onClick={requestPropertyData} disabled={loading || !selected}>Request Property Data</button>
        <button className="px-3 py-2 rounded border" onClick={fetchPropertyResult} disabled={loading || !requestId}>Fetch Property Result</button>
      </div>

      {status && (
        <div className="mt-4" style={{ color: status.startsWith('✅') ? '#16a34a' : status.startsWith('❌') ? '#dc2626' : '#eab308' }}>{status}</div>
      )}

      {/* CB:BEGIN ev-measurement-failsafe-ui */}
      {measurementSummary && (
        <div className="mt-3 bg-white border rounded-lg p-3">
          <div className="font-semibold mb-2">Measurement Report Summary (local)</div>
          <div className="text-slate-700 text-sm">ReportId: <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{measurementSummary.reportId}</span></div>
          <div className="text-slate-700 text-sm">Status: {measurementSummary.status}</div>
          <div className="text-sm" style={{ color: measurementSummary.emailed ? '#16a34a' : '#dc2626' }}>
            Email: {measurementSummary.emailed ? 'sent' : 'failed'}
          </div>
          {!measurementSummary.emailed && measurementSummary.emailError && (
            <div className="text-xs text-slate-500 mt-1">Email error: {measurementSummary.emailError}</div>
          )}
        </div>
      )}

      {measurementJson && (
        <details className="mt-2">
          <summary className="cursor-pointer text-slate-700 text-sm">Show raw GetReport JSON</summary>
          <pre className="mt-2 bg-slate-900 text-slate-200 p-3 rounded-lg overflow-x-auto text-xs">
{JSON.stringify(measurementJson, null, 2)}
          </pre>
        </details>
      )}
      {pdfUrl && (
        <div className="mt-3">
          <div className="font-semibold mb-2">Report PDF Preview</div>
          <iframe src={pdfUrl} style={{ width: '100%', height: 480, border: '1px solid #e5e7eb', borderRadius: 8 }} />
          <div className="mt-2">
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">Open PDF in new tab</a>
          </div>
        </div>
      )}
      {/* CB:END ev-measurement-failsafe-ui */}

      {propertyJson && (
        <pre className="mt-4 bg-slate-900 text-slate-200 p-3 rounded-lg overflow-x-auto text-xs">
{JSON.stringify(propertyJson, null, 2)}
        </pre>
      )}
    </div>
  );
};

export default EagleViewSandbox;
// CB:END eagleview-sandbox-page


